import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import {
    Box,
    Typography,
    Grid2,
    Stack,
    TextField,
    InputAdornment,
    IconButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getActivityMasterList } from "../../services/timeTracker.service";
import { encryptData } from "../../utils/cryptoUtils";
import MasterCommonTable from "../../sharedComponents/MaterCommonTable/MasterCommonTable";

const AdminMasterTable = ({ onUserSelect, selectedDate, setSelectedDate }) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");

    const fetchMasterTableData = async (date) => {
        try {
            setLoading(true);
            setError(null);
            const formattedDate = dayjs(date).format("YYYY-MM-DD");
            const encryptedDate = encryptData(formattedDate);
            const res = await getActivityMasterList(encryptedDate);

            // Response interceptor unwraps axios → res is already { error, data, ... }.
            const rows = res?.data || [];

            const transformedData = rows.map((user) => {
                const totalActiveMinutes = user.totalActiveMinutes || 0;
                const workLogMinutes = user.workLogMinutes || 0;
                const differenceMinutes = Math.abs(totalActiveMinutes - workLogMinutes);

                return {
                    ...user,
                    workingTime: formatDuration(totalActiveMinutes),
                    workLogTime: formatDuration(workLogMinutes),
                    timeDifference: formatDuration(differenceMinutes),
                    timeDifferenceMinutes: differenceMinutes,
                    avgActivity: `${user.averageActivity || 0}%`,
                    startTime: user.startAt ? dayjs(user.startAt).format("hh:mm A") : "-",
                    breakTime: formatDuration(Math.round((user.breakSec || 0) / 60)),
                    clockOutTime: user.clockOutAt ? dayjs(user.clockOutAt).format("hh:mm A") : "-",
                };
            });

            setData(transformedData);
        } catch (error) {
            console.error("Error fetching master table data:", error);
            setError("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMasterTableData(selectedDate);
    }, [selectedDate]);

    const formatDuration = (totalMinutes) => {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0"
        )}`;
    };

    const handleRowClick = (row) => {
        onUserSelect(row.userId, row.userName);
    };

    const filteredData = (() => {
        const q = search.trim().toLowerCase();
        if (!q) return data;
        return data.filter((row) => {
            const name = String(row.userName || "").toLowerCase();
            const email = String(row.email || "").toLowerCase();
            return name.includes(q) || email.includes(q);
        });
    })();

    // Define table columns
    const columns = [
        { id: "userName", label: "Name" },
        { id: "startTime", label: "Start Time" },
        { id: "breakTime", label: "Break Time" },
        { id: "clockOutTime", label: "Clock Out Time" },
        { id: "workingTime", label: "Working Time" },
        { id: "workLogTime", label: "Work Log Time" },
        { id: "timeDifference", label: "Difference" },
        { id: "avgActivity", label: "Avg. Activity" },
        // { id: "recordCount", label: "Records" },
    ];

    // Table configuration
    const tableConfig = {
        isAction: false,
        isView: true,
        isEdit: false,
        isDelete: false,
        isToggle: false,
        isApprove: false,
        isReject: false,
        isPdfView: false,
        isMore: false,
        isHistory: false,
        isAvailableLeave: false,
        isAdmin: false,
        isClient: false,
    };

    return (
        <Box sx={{ padding: 3 }}>
            <Grid2 container mb={3}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Typography variant="h5" fontWeight="bold" mb={2}>
                        Time Tracker - Master View
                    </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }}>
                    <Stack direction="row" justifyContent="flex-end">
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={["DatePicker"]}>
                                <DatePicker
                                    value={selectedDate}
                                    className="datePicker"
                                    format="DD/MM/YYYY"
                                    onChange={(date) => setSelectedDate(date)}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                    </Stack>
                </Grid2>
            </Grid2>

            {error && (
                <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                    <Typography color="error">{error}</Typography>
                </Box>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                <TextField
                    size="small"
                    placeholder="Search by name or email…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    sx={{ width: { xs: "100%", sm: 320 } }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                        endAdornment: search ? (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={() => setSearch("")} title="Clear">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                />
                {search && (
                    <Typography variant="caption" color="text.secondary">
                        {filteredData.length} of {data.length}
                    </Typography>
                )}
            </Stack>

            <MasterCommonTable
                columns={columns}
                data={filteredData}
                handleView={handleRowClick}
                tableConfig={tableConfig}
                openLoader={loading}
                isServerSide={false}
                isPaginationBar={false}
            />
        </Box>
    );
};

export default AdminMasterTable;
